'use client';

import { usePathname, useRouter } from 'next/navigation';
import { FC, PropsWithChildren, createContext, use, useEffect, useMemo, useRef, useState, useTransition } from 'react';

export const DELAY = 250;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(() => resolve(), ms));
const noop = () => {};

type TransitionContext = {
  pending: boolean;
  navigate: (url: string, isReverse?: boolean) => void;
  pathname: string;
  reverse: boolean;
};
export const FormStepTransitionContext = createContext<TransitionContext>({
  pending: false,
  navigate: noop,
  pathname: '/',
  reverse: false,
});
export const useFormStepTransition = () => use(FormStepTransitionContext);

export const FormStepTransitionProvider: FC<PropsWithChildren> = ({ children }) => {
  const [pending, start] = useTransition();
  const pathname = usePathname();
  const prevUrl = useRef<string>(pathname ?? '/');
  const [navigation, setNavigation] = useState<[string, boolean]>([pathname ?? '/', false]);
  const router = useRouter();

  useEffect(() => {
    if (prevUrl.current !== navigation[0]) {
      start(async () => {
        await sleep(DELAY);
        prevUrl.current = navigation[0];
        router.push(navigation[0]);
      });
    }
  }, [navigation, start]);

  const navigate = (href: string, isReverse?: boolean) => {
    setNavigation([href, !!isReverse]);
  };

  const contextValues: TransitionContext = useMemo(
    () => ({ pending, navigate, pathname: navigation[0], reverse: navigation[1] }),
    [pending, navigate, navigation],
  );

  return <FormStepTransitionContext.Provider value={contextValues}>{children}</FormStepTransitionContext.Provider>;
};
